/**
 * GraphQL queries and hooks — team pillar.
 * Split from graphql/queries.ts (issue #1409).
 */

import { gql } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';
import { useAllPages } from './useAllPages';
import type { Team, TeamConnection, TeamFilter, TeamHierarchyNode } from '../types/team';

/**
 * Fragment for Team fields
 */
export const TEAM_FIELDS = gql`
  fragment TeamFields on Team {
    entityId
    entityType
    name
    description
    type
    parentTeamId
    leadUserId
    memberIds
    responsibilities
    contactEmail
    teamsChannel
    _schemaVersion
    _createdAt
    _createdBy
    _updatedAt
    _updatedBy
    _deletedAt
  }
`;

/**
 * Query to get a single team by ID
 */
export const GET_TEAM = gql`
  ${TEAM_FIELDS}
  query GetTeam($id: ID!) {
    team(id: $id) {
      ...TeamFields
      parentTeam {
        ...TeamFields
      }
      childTeams {
        ...TeamFields
      }
    }
  }
`;

/**
 * Query to list all teams with filtering and pagination
 */
export const LIST_TEAMS = gql`
  ${TEAM_FIELDS}
  query ListTeams($filter: TeamFilter, $first: Int, $after: String) {
    teams(filter: $filter, first: $first, after: $after) {
      edges {
        node {
          ...TeamFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

/**
 * Query to get team hierarchy
 */
export const GET_TEAM_HIERARCHY = gql`
  ${TEAM_FIELDS}
  query GetTeamHierarchy($teamId: ID!, $maxDepth: Int) {
    teamHierarchy(teamId: $teamId, maxDepth: $maxDepth) {
      team {
        ...TeamFields
      }
      children {
        team {
          ...TeamFields
        }
        children {
          team {
            ...TeamFields
          }
          children {
            team {
              ...TeamFields
            }
            depth
          }
          depth
        }
        depth
      }
      depth
    }
  }
`;

/**
 * Hook to get a single team
 */
export function useTeam(id: string): QueryResult<{ team: Team | null }> {
  return useQuery(GET_TEAM, {
    variables: { id },
    skip: !id,
  });
}

/**
 * Hook to list teams with optional filtering
 */
export function useTeams(
  filter?: TeamFilter,
  first: number = 50
): QueryResult<{ teams: TeamConnection }> {
  return useQuery(LIST_TEAMS, {
    variables: { filter, first },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

/**
 * Hook to list every team (all pages, not capped at one server page).
 *
 * Used alongside {@link useAllServices} to build the shared
 * service->{name, teamId, teamName} resolution map (issue #495). See
 * {@link useAllPages}.
 */
export function useAllTeams(
  filter?: TeamFilter,
  first: number = 250
): QueryResult<{ teams: TeamConnection }> {
  return useAllPages<{ teams: TeamConnection }>(
    LIST_TEAMS,
    (data) => data?.teams,
    { filter },
    first
  );
}

/**
 * Hook to get team hierarchy
 */
export function useTeamHierarchy(
  teamId: string,
  maxDepth: number = 3
): QueryResult<{ teamHierarchy: TeamHierarchyNode | null }> {
  return useQuery(GET_TEAM_HIERARCHY, {
    variables: { teamId, maxDepth },
    skip: !teamId,
  });
}
